import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { tr } from 'date-fns/locale';
import moment, { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { FeatureKey, FeatureService } from 'src/app/services/feature/feature.service';
import { DomainEnum, LogFonctionnelGet, LogFonctionnelService, ResultEnum } from 'src/app/services/log-fonctionnel/log-fonctionnel.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { HeaderCommunicationService } from 'src/app/services/node/header-communication.service';
import { CabinetService } from 'src/app/services/office/cabinet.service';
import { PatientService } from 'src/app/services/patient/patient.service';
import { IdentityHistoricProblem, RaisonEnum, RechercheAnomaliesService } from 'src/app/services/patient/recherche-anomalie.service';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Header } from 'src/app/utils/interfaces/new/header.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';

interface LogTableDisplay {
  moment: string;
  actor: string;
  target: string;
  action: string;
  result: string;
  detail: string;
  subDomain: string;
  rpps?: string;
}

interface SelectInput {
  id: string;
  libelle: string;
}

@Component({
  selector: 'app-logs-applicatifs',
  templateUrl: './logs-applicatifs.component.html',
  styleUrls: ['./logs-applicatifs.component.scss']
})
export class LogsApplicatifsComponent implements OnInit, OnDestroy {

  public pageTitle = 'Logs applicatifs';

  public domainsListForm: FormGroup;
  public domainsList: Array<string> = [];
  private _domainChangeSub: Subscription;
  private _currentDomain: string;
  public resultsList: Array<SelectInput> = [];
  private _currentResult: string;
  private _currentDateStart: Moment;
  private _currentDateEnd: Moment;
  private _currentAction: string;
  public actionsList: Array<string> = [];
  private _pageFiltresInitial: Filtres = {
    perpage: 30,
    currentPage: 1,
    sortOrder: SortEnum.DESC,
    sortBy: 'timestamp'
  };
  private _pageFiltres = this._pageFiltresInitial;

  public logsTable: Paginated<LogTableDisplay>;
  public logsTableColumnTable = [
    { title: 'Date et heure', data: 'moment' },
    { title: 'Sous domaine', data: 'subDomain' },
    { title: 'Acteur', data: 'actor' },
    { title: 'Patient', data: 'target' },
    { title: 'Action', data: 'action' },
    { title: 'RPPS', data: 'rpps' },
    { title: 'Résultat', data: 'result' },
    { title: 'Détails', data: 'detail' },
  ];

  public displayRechercheAnomalies = false;
  public displayedColumns: string[] = ['nomPrenom', 'anomalies'];
  public listAnomalies: Array<IdentityHistoricProblem> = [];
  public anomalieRaisonEnumTpl = RaisonEnum;

  constructor(
    private _send: HeaderCommunicationService,
    private _formBuilder: FormBuilder,
    private _logSrv: LogFonctionnelService,
    private _patientSrv: PatientService,
    private _cabinetSrv: CabinetService,
    private _modalSrv: ModalService,
    private _view: ViewContainerRef,
    private _featureSrv: FeatureService,
    private _anomalieSrv: RechercheAnomaliesService,
    private _router: Router,
  ) {
    const head: Header = {
      backPage: 'utilitaires',
      isBack: true,
      title: this.pageTitle,
    };
    this._send.announceHeader(head);
  }

  async ngOnInit(): Promise<void> {
    await this._initData();

    this._domainChangeSub = this.domainsListForm.valueChanges.subscribe(async d => {
      if (d.domain && this._currentDomain !== d.domain) {
        this._currentAction = undefined;
        this.actionsList = await this._logSrv.getActionsFromDomain(d.domain as DomainEnum) || [];
      } else {
        this._currentAction = d.action || undefined;
      }

      this._currentDomain = d.domain;
      this._currentResult = d.result || undefined;
      this._currentDateStart = d.dateStart || undefined;
      this._currentDateEnd = d.dateEnd || undefined;
      this._changeDomain();
    });

    await this._initDataAnomalie()
  }

  private async _initDataAnomalie(): Promise<void> {
    try {
      this.displayRechercheAnomalies = await this._featureSrv.canUseFeature(FeatureKey.RECHERCHE_ANOMALIE_IDENTITES);
    } catch (e) {
      console.error(e);
      this._modalSrv.openAlert(
        this._view, 'Erreur',
        e.message || e.error?.message || 'Une erreur est survenue'
      );
    }
  }

  public async searchAnomalies(): Promise<void> {
    const loader = this._modalSrv.openLoading(this._view);
    try {
      if (!await this._featureSrv.canUseFeature(FeatureKey.RECHERCHE_ANOMALIE_IDENTITES)) {
        this._modalSrv.openAlert(this._view, 'Erreur', 'Vous n\'avez pas les droits pour effectuer cette action');
        return;
      }

      const anomalies = await this._anomalieSrv.rechercherAnomalies();
      this.listAnomalies = anomalies.sort((a, b) => b.raisons.length - a.raisons.length);
    } catch (e) {
      console.error(e);
      this._modalSrv.openAlert(
        this._view, 'Erreur',
        e.message || e.error?.message || 'Une erreur est survenue'
      );
    } finally {
      if (loader) {
        loader.destroy();
      }
    }
  }

  public navigateToPatient(patient: IdentityHistoricProblem): void {
    const urlTree = this._router.createUrlTree([`/user/patient/${patient.patientId}`]);
    const serializedUrl = this._router.serializeUrl(urlTree);
    window.open(serializedUrl, '_blank');
  }

  private async _initData(): Promise<void> {
    try {
      this.domainsList = Object.values(DomainEnum);
      this.resultsList = [
        { id: ResultEnum.Success, libelle: 'Succès' },
        { id: ResultEnum.Error, libelle: 'Erreur' },
        { id: ResultEnum.Unknown, libelle: 'Inconnu' },
      ];

      if (this.domainsList && this.domainsList[0]) {
        this.actionsList = await this._logSrv.getActionsFromDomain(this.domainsList[0] as DomainEnum) || [];
      }
  
      this.domainsListForm = this._formBuilder.group({
        domain: [this.domainsList && this.domainsList.length > 0 ? this.domainsList[0] : ''],
        result: [undefined],
        dateStart: [undefined],
        dateEnd: [undefined],
        action: [this.actionsList && this.actionsList.length > 0 ? this.actionsList[0] : undefined],
      });
    } catch (e) {
      console.error(e);
      this._modalSrv.openAlert(
        this._view, 'Erreur',
        e.message || e.error?.message || 'Une erreur est survenue'
      );
    }
  }

  ngOnDestroy(): void {
    if (this._domainChangeSub) {
      this._domainChangeSub.unsubscribe();
    }
  }

  private async _changeDomain(): Promise<void> {
    this._pageFiltres = this._pageFiltresInitial;
    await this._loadLogs();
  }

  private async _loadLogs(): Promise<void> {
    const _logsApiToLogsTable = async (logsApi: Array<LogFonctionnelGet>): Promise<Array<LogTableDisplay>> => {
      const rtd: Array<LogTableDisplay> = [];
      for (const l of logsApi) {
        rtd.push({
          action: l.action,
          actor: await this._getProfilLabel(l.idActor),
          moment: l.timestamp ? moment(l.timestamp).format('DD/MM/YYYY HH:mm:ss') : '',
          detail: l.detail || '',
          result: l.result,
          target: l.idTarget && l.idTarget !== '' ? await this._getPatientLabel(l.idTarget) : '',
          subDomain: l.subDomain,
          rpps: l.rpps || ''
        });
      }

      return rtd;
    };

    if (!this._currentDomain || this._currentDomain === '') {
      return;
    }

    const logs = await this._logSrv.getLogs({
      domain: this._currentDomain,
      result: this._currentResult,
      dateStart: this._currentDateStart ? this._currentDateStart.toDate() : undefined,
      dateEnd: this._currentDateEnd ? this._currentDateEnd.toDate() : undefined,
      action: this._currentAction || undefined,
    }, this._pageFiltres);

    const logsMap: Array<LogTableDisplay> = await _logsApiToLogsTable(logs && logs.objects ? logs.objects : []);

    if (this.logsTable) {
      this.logsTable.objects = logsMap;
      this.logsTable.page = logs.page;
      this.logsTable.perpage = logs.perpage;
      this.logsTable.total = logs.total;
    } else {
      this.logsTable = {
        objects: logsMap,
        page: logs.page,
        perpage: logs.perpage,
        total: logs.total,
      };
    }
  }

  public async onChangePage(value): Promise<void> {
    this._pageFiltres.currentPage = value;
    await this._loadLogs();
  }

  private _patientsMap: Map<string, string> = new Map<string, string>();
  private async _getPatientLabel(patientId: string): Promise<string> {
    if (this._patientsMap.get(patientId)) {
      return this._patientsMap.get(patientId);
    } else {
      const patient = await this._patientSrv.findOnePatientFicheForLogs(patientId);
      if (patient) {
        this._patientsMap.set(patientId, `${patient.nom} ${patient.prenom}`);
      } else {
        this._patientsMap.set(patientId, ``);
      }
      return this._patientsMap.get(patientId);
    }
  }

  private _profils: Array<{ id: string, prenom: string, nom: string }>;
  private _profilsMap: Map<string, string> = new Map<string, string>();
  private async _getProfilLabel(profilId: string): Promise<string> {
    if (!profilId) return '';
    
    if (this._profilsMap.has(profilId)) {
      return this._profilsMap.get(profilId);
    }

    if (!this._profils || this._profils.length === 0) {
      this._profils = await this._cabinetSrv.findCurrentCabinetDestinataireForLogs() || [];
    }

    const profil = this._profils.find(p => p.id === profilId);
    const label = profil ? `${profil.nom} ${profil.prenom}` : '';
    this._profilsMap.set(profilId, label);
    return label;
  }

  public async export() {
    const filtres = {
      perpage: -1,
      currentPage: 1,
      sortOrder: SortEnum.DESC,
      sortBy: 'timestamp'
    };
    const loader = this._modalSrv.openLoading(this._view);
    try {
      // Récupération de tous les logs (via filtres actuels)
      const logs = await this._logSrv.getLogs({
        domain: this._currentDomain,
        result: this._currentResult,
        dateStart: this._currentDateStart ? this._currentDateStart.toDate() : undefined,
        dateEnd: this._currentDateEnd ? this._currentDateEnd.toDate() : undefined,
        action: this._currentAction || undefined,
      }, filtres);

      const rowsData: Array<string[]> = [];
      const logsObjects = logs && logs.objects ? logs.objects : [];
      for (const l of logsObjects) {
        rowsData.push([
          l.timestamp ? moment(l.timestamp).format('DD/MM/YYYY HH:mm:ss') : '',
          l.subDomain || '',
          await this._getProfilLabel(l.idActor),
          l.idTarget && l.idTarget !== '' ? await this._getPatientLabel(l.idTarget) : '',
          l.action,
          l.rpps || '',
          l.result,
          (l.detail || '').replace(/(\r\n|\n|\r|;)/gm, ' ') // Nettoyage pour le CSV
        ]);
      }

      // Construction de la chaîne CSV
      const header = ["Date", "Sous-domaine", "Acteur", "Patient", "Action", "RPPS", "Resultat", "Details"].join(";");
      const csvContent = "\ufeff" + header + "\r\n" + rowsData.map(r => r.join(";")).join("\r\n");

      // Téléchargement
      this.saveAsFile(csvContent, 'export_logs_applicatifs.csv', 'text/csv;charset=utf-8');
    } catch (e) {
      this._modalSrv.openAlert(this._view, 'Erreur', "Une erreur est survenue lors de l'export.");
      console.error(e);
    } finally {
      if (loader) {
        loader.destroy();
      }
    }
  }

  private saveAsFile(buffer: any, fileName: string, fileType: string): void {
    const data: Blob = new Blob([buffer], { type: fileType });
    var downloadURL = window.URL.createObjectURL(data);
    var link = document.createElement('a');
    link.href = downloadURL;
    link.download = fileName;
    link.click();
  }
}
